import { useContext, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router";
import { AppContext, ServicesContext } from "../../contexts";
import { useTranslation } from "../../hooks";
import { determineOtpMethod, determineView, stampWorkOrderRef } from "../../utils";
import useDirectLogin from "../../hooks/useDirectLogin";

function useLogin(confirmationNumberField, otpField) {
  const { i18n } = useTranslation();
  const { current: retryTimeout } = useRef(30);
  const { setAppointmentData, errorMessage, setErrorMessage } = useContext(AppContext);
  const { tryToFetchWithoutLoggingIn, sendOtp, verifyAndFetch } = useContext(ServicesContext)
  const { current: defaultConfirmationNumber } = useRef(new URLSearchParams(window.location.search).get('ref'));
  const { current: haveExpiry } = useRef(new URLSearchParams(window.location.search).get('have_expiry'));
  const { directLoginCheck } = useDirectLogin(defaultConfirmationNumber);
  const retryCounter = useRef();
  const [isDeterminingValidLogin, setIsDeterminingValidLogin] = useState(true);
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [isConfirmationNumberValid, setIsConfirmationNumberValid] = useState(!!defaultConfirmationNumber);
  const [otpMessage, setOtpMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [retriedTimes, setRetriedTimes] = useState(0);
  const [otpMethod, setOtpMethod] = useState();
  const [isOtpSending, setIsOtpSending] = useState(false);
  const [isOtpValidating, setIsOtpValidating] = useState(false);
  const navigate = useNavigate();

  const validate = (e) => {
    setIsConfirmationNumberValid(!!e.currentTarget.elements[0].value);
    setIsOtpValid(!!e.currentTarget.elements[1].value);
  }

  const handleGetOtp = async () => {
    if (!confirmationNumberField.current.value) return;
    setIsOtpSending(true);
    setRetriedTimes(retriedTimes + 1);

    const method = determineOtpMethod(otpMethod, retriedTimes);
    const { status, message, otpType } = await sendOtp(confirmationNumberField.current.value, method);

    setIsOtpSending(false);
    if (status) {
      setRetryCount(retryTimeout);
      setOtpMessage(message);
      setOtpMethod(otpType);
      setErrorMessage();
    } else {
      setErrorMessage(message);
      setOtpMessage('');
    }
  }

  const handleSubmit = async () => {
    setIsOtpValidating(true);

    try {
      const { status, bookingDetails, statuscode } = await verifyAndFetch(confirmationNumberField.current.value, otpField.current.value);

      if (status) {
        setAppointmentData(bookingDetails);
  
        if (bookingDetails?.cancelledTask) {
          navigate('/message', {
            state: {
              title: bookingDetails.newLabel['cp_appointment_cancelled'] || i18n('cp_appointment_cancelled'),
              copy:  bookingDetails.newLabel['cp_task_cancelled'] || i18n('cp_task_cancelled'),
              status: false, 
              errorMessage: i18n('cp_task_cancelled'),
            }
          });
          return;
        }

        stampWorkOrderRef(bookingDetails);
        navigate(determineView(bookingDetails));
      } else if (statuscode === 400){
        setErrorMessage(i18n('cp_otp_error'));
        setOtpMessage('');
      } else {
        setErrorMessage(i18n('cp_fallback_error'));
      }
    } catch (error) {
    } finally {
      setIsOtpValidating(false);
    }
  };

  const decreaseRetryCount = () => {
    if (!retryCount) {
      clearTimeout(retryCounter?.current);
    } else {
      retryCounter.current = setTimeout(setRetryCount.bind(null, retryCount - 1), 1000);
    }
  }

  useEffect(() => {
    decreaseRetryCount();
    return () => {
      clearTimeout(retryCounter?.current);
    }
  }, [retryCount]);

  useEffect(() => {
    async function _tryToFetchWithoutLoggingIn() {
      const { status, bookingDetails } = await tryToFetchWithoutLoggingIn(defaultConfirmationNumber);
      if (status) {
        setAppointmentData(bookingDetails);
        stampWorkOrderRef(bookingDetails);
        navigate(determineView(bookingDetails));
      } else {
        directLoginCheck();
        setIsDeterminingValidLogin(false);
      };
    }
    _tryToFetchWithoutLoggingIn();
  }, [])

  return {
    defaultConfirmationNumber,
    validate,
    isConfirmationNumberValid,
    isOtpValid,
    otpMessage,
    retryCount,
    isOtpSending,
    isOtpValidating,
    errorMessage,
    handleSubmit,
    handleGetOtp,
    isDeterminingValidLogin,
    haveExpiry
  }
}

export default useLogin;
